import { useQuery } from '@tanstack/react-query'
import { config, hasR2 } from '@/config'
import type { ProductFeed } from './types'
import { normalizeProductFeed } from './normalize'

/**
 * Fetch + normalize an R2 product feed. The parsing is the pure, tested
 * `normalizeProductFeed` (see ./normalize.ts); this module is just the I/O +
 * React-Query adapter. Supported shapes include the `bands[].frames[].t` manifest.
 */
export async function fetchProductFeed(url: string): Promise<ProductFeed> {
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error(`Feed ${res.status}: ${url}`)
  const json = await res.json()
  return normalizeProductFeed(json, baseOf(url))
}

/** Origin of the feed URL, used to resolve relative frame URLs. */
function baseOf(url: string): string {
  try {
    const u = new URL(url)
    return `${u.protocol}//${u.host}`
  } catch {
    return config.r2.base
  }
}

/** Query hook for a product feed. Disabled (and surfaces a config hint) until R2 is set. */
export function useProductFeed(feedUrl: string | undefined) {
  return useQuery({
    queryKey: ['feed', feedUrl],
    queryFn: () => fetchProductFeed(feedUrl!),
    enabled: hasR2 && !!feedUrl,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })
}
